import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import CopyFieldIcon from "components/CopyField/Icon";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { generateExampleTrustPolicyStatement } from "./constants";
import { GET_SPACELIFT_AWS_ACCOUNT_ID } from "./gql";
import styles from "./styles.module.css";

type AwsIntegrationTrustPolicyExampleProps = {
  tagAssumeRole: boolean;
  showWarning: boolean;
};

const AwsIntegrationTrustPolicyExample = ({
  tagAssumeRole,
  showWarning,
}: AwsIntegrationTrustPolicyExampleProps) => {
  const { accountName } = useTypedContext(AccountContext);
  const { data } = useQuery(GET_SPACELIFT_AWS_ACCOUNT_ID);

  const parsedAssumeRolePolicyStatement = useMemo(() => {
    if (data?.spaceliftAwsAccountId) {
      return JSON.stringify(
        generateExampleTrustPolicyStatement(accountName, data.spaceliftAwsAccountId, tagAssumeRole),
        null,
        2
      );
    }

    return null;
  }, [accountName, data, tagAssumeRole]);

  return (
    <Banner
      variant={showWarning ? "warning" : "info"}
      title="Configure the trust policy for the IAM role"
    >
      {showWarning && (
        <Typography tag="p" variant="p-body3" margin="large 0 0 0">
          <b>
            {tagAssumeRole
              ? "The IAM role's trust policy must be updated in AWS to include the new TagSession statement."
              : "The IAM role's trust policy must be updated in AWS to remove the TagSession statement."}
          </b>
        </Typography>
      )}

      <Typography tag="p" variant="p-body3" margin="large 0 0 0">
        Here's an example trust policy statement you can use, that allows any stack within your
        Spacelift account to use the IAM Role:
      </Typography>

      {parsedAssumeRolePolicyStatement && (
        <Box className={styles.codeWrapper} margin="small 0 0 0">
          <CopyFieldIcon
            className={styles.codeCopyButton}
            value={parsedAssumeRolePolicyStatement}
            title="Copy"
            variant="secondary"
          />
          <pre className={styles.code}>{parsedAssumeRolePolicyStatement}</pre>
        </Box>
      )}
    </Banner>
  );
};

export default AwsIntegrationTrustPolicyExample;
